import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Zap, Heart } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#09090b] text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#00ff9d] font-bold text-sm uppercase tracking-widest">About Us</span>
            <h1 
              className="font-heading font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mt-4"
              data-testid="about-title"
            >
              GOAttitude
            </h1>
            <p className="text-zinc-400 text-lg mt-6 leading-relaxed">
              The combination of G.O.A.T., striving to be the Greatest Of All Time in your own journey, and attitude, the mindset and approach you bring to your training, preparation, and everyday life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
                <img
                  src="https://res.cloudinary.com/dlqzefke5/image/upload/v1769068196/DSC05979-Enhanced-NR_i4oqvp.webp"
                  alt="G.O.A.T. Athletic"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <span className="text-[#ff4d6d] font-bold text-sm uppercase tracking-widest">Our Story</span>
              <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mt-2">
                Built on Belief
              </h2>
              <div className="mt-6 space-y-4 text-zinc-600 leading-relaxed">
                <p>
                  G.O.A.T. Athletic was created from the belief that greatness is built through mindset, belief, and consistent effort. At the core of our brand is GOAttitude — the combination of G.O.A.T., striving to be the Greatest Of All Time in your own journey, and attitude, the mindset and approach you bring to your training, preparation, and everyday life. It represents the belief that greatness is shaped by how you think, how you show up, and how consistently you commit to growth.
                </p>
                <p>
                  Every product we create carries meaning. The words, phrases, and details built into our gear are intentional — designed to reinforce self-belief, inspire confidence, and encourage athletes to step into the best version of themselves. G.O.A.T. Athletic is more than apparel; it's a reminder of the standards you set and the mindset you choose every day.
                </p>
                <p>
                  Born out of real sport, real training, and lived experience, G.O.A.T. Athletic supports athletes at every stage of their journey — from grassroots to elite, from training sessions to competition and everyday life. We believe greatness is earned through commitment, consistency, and growth. GOAttitude is the mindset that drives that journey forward.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#00ff9d] font-bold text-sm uppercase tracking-widest">What We Stand For</span>
            <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mt-2">
              Our Pillars
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Performance',
                description: 'Performance starts with preparation and is sustained through consistency. Guided by GOAttitude, our gear is designed to support the physical demands athletes place on their bodies, delivering comfort, durability, and a fit that allows you to move freely and confidently.\n\nEvery detail is considered, from materials to construction, so athletes can stay focused on training, execution, and improvement. When your gear performs, you can perform with confidence.'
              },
              {
                icon: Zap,
                title: 'Athlete Mindset',
                description: 'GOAttitude is about how you think, train, and respond. The words and messages integrated into G.O.A.T. Athletic products are intentional — designed to reinforce belief, sharpen focus, and encourage athletes to trust the work they are putting in.\n\nThese mindset prompts serve as daily reminders to stay disciplined, stay present, and stay committed to growth. Being G.O.A.T. isn\'t about comparison — it\'s about ownership, progress, and becoming better than yesterday.'
              },
              {
                icon: Heart,
                title: 'Style',
                description: 'Style is an extension of identity. G.O.A.T. Athletic blends performance-driven design with a clean, modern aesthetic that reflects GOAttitude — confident, purposeful, and authentic.\n\nOur designs move seamlessly from training to everyday wear, allowing athletes to represent what they stand for wherever they go. Every piece is created with intent, so style never comes at the cost of performance or meaning.'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border-2 border-zinc-200 hover:border-black transition-colors"
              >
                <value.icon className="w-10 h-10 text-[#00ff9d] mb-4" />
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight">
                  {value.title}
                </h3>
                <p className="text-zinc-600 mt-3 whitespace-pre-line">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#09090b] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mb-6">
              Ready to Join the Movement?
            </h2>
            <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10">
              Discover gear built for athletes who refuse to settle.
            </p>
            <Link 
              to="/shop" 
              className="btn-accent inline-flex items-center gap-2 neon-glow group"
              data-testid="about-shop-btn"
            >
              Shop Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
